import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button, SelectDropdown, Table, Td, Th, Tip, Tr, useTip, WrappedPagination } from '@makyoapp/makyo-ui'

import { ReactComponent as FileTextIcon } from '@/assets/vectors/icon_lucide_document.svg?react'
import { ReactComponent as IconLayout } from '@/assets/vectors/icon_lucide_layout.svg?react'
import { ReactComponent as PackageIcon } from '@/assets/vectors/icon_lucide_package.svg?react'
import { ReactComponent as PeopleIcon } from '@/assets/vectors/icon_lucide_single_person.svg?react'

import { BillingInvoice } from './BillingInvoice'

interface Payment {
  id: number
  date: string
  amount: string
  status: string
  cardLast4: string
  type: 'individual' | 'package' | 'layout'
  invoiceNumber: string
  subscription: {
    basePlan: { name: string; amount: string }
    addons: Array<{ name: string; quantity: number; unitPrice: string; amount: string }>
    total: string
  }
}

const generateRandomPayment = (id: number, date: string): Payment => {
  const plans = [
    { name: 'Free Plan', amount: '$0.00' },
    { name: 'Basic Plan', amount: '$39.99' },
    { name: 'Pro Plan', amount: '$49.99' },
    { name: 'Enterprise Plan', amount: '$99.99' },
  ]

  const addons = [
    { name: 'Extra Published App', unitPrice: '$20.00' },
    { name: 'Extra Member', unitPrice: '$10.00' },
    { name: 'Extra Storage (1 GB)', unitPrice: '$5.00' },
    { name: 'Extra Storage (3 GB)', unitPrice: '$15.00' },
    { name: 'Premium Support', unitPrice: '$25.00' },
    { name: 'Custom Domain', unitPrice: '$8.00' },
  ]

  const cardNumbers = ['4242', '5555', '8210', '9876', '1234', '6789']
  const types: Array<'individual' | 'package' | 'layout'> = ['individual', 'package', 'layout']

  // Random selections
  const selectedPlan = plans[Math.floor(Math.random() * plans.length)]
  const numberOfAddons = Math.floor(Math.random() * 10)
  const selectedCard = cardNumbers[Math.floor(Math.random() * cardNumbers.length)]
  const selectedType = types[Math.floor(Math.random() * types.length)]

  // Generate random addons
  const selectedAddons = []
  for (let i = 0; i < numberOfAddons; i++) {
    const addon = addons[Math.floor(Math.random() * addons.length)]
    const quantity = Math.floor(Math.random() * 3) + 1 // 1 to 3
    const amount = `$${(parseFloat(addon.unitPrice.replace('$', '')) * quantity).toFixed(2)}`
    selectedAddons.push({
      name: addon.name,
      quantity,
      unitPrice: addon.unitPrice,
      amount,
    })
  }

  // Calculate total
  const total = (
    parseFloat(selectedPlan.amount.replace('$', '')) +
    selectedAddons.reduce((sum, addon) => sum + parseFloat(addon.amount.replace('$', '')), 0)
  ).toFixed(2)

  return {
    id,
    date,
    amount: `$${total}`,
    status: 'Paid',
    cardLast4: selectedCard,
    type: selectedType,
    invoiceNumber: `INV-${date.slice(0, 4)}-${String(id).padStart(3, '0')}`,
    subscription: {
      basePlan: selectedPlan,
      addons: selectedAddons,
      total: `$${total}`,
    },
  }
}

// Generate mock data with custom amount
const generateMockData = (amount: number): Payment[] => {
  const payments: Payment[] = []

  // Calculate start date based on amount of data
  // We'll generate one payment per day, going backwards from today
  const startDate = new Date()

  for (let i = 0; i < amount; i++) {
    const currentDate = new Date(startDate)
    currentDate.setDate(startDate.getDate() - i) // Go back i days
    const dateString = currentDate.toISOString().split('T')[0]
    payments.push(generateRandomPayment(i + 1, dateString))
  }

  // Sort by date in descending order
  return payments.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
}

// Generate mock data with desired amount
const allPayments: Payment[] = generateMockData(50000) // Generate 10000 payments

interface PaymentRowProps {
  payment: Payment
  formatDate: (date: string) => string
  onOpenInvoice: (payment: Payment) => void
  t: (key: string) => string
}

const PaymentRow = ({ payment, formatDate, onOpenInvoice, t }: PaymentRowProps) => {
  const tipProps = useTip()
  return (
    <Tr>
      <Td>
        {payment.type === 'individual' && (
          <>
            <div
              ref={tipProps.refs.setReference}
              className='flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 text-blue-500'
              {...tipProps.getReferenceProps()}
            >
              <PeopleIcon />
            </div>
            {tipProps.isOpen && (
              <Tip
                x={tipProps.x}
                y={tipProps.y}
                placement={tipProps.placement}
                strategy={tipProps.strategy}
                ref={tipProps.refs.setFloating}
                context={tipProps.context}
                arrowRef={tipProps.arrowRef}
                {...tipProps.getFloatingProps()}
              >
                {t('Membership')}
              </Tip>
            )}
          </>
        )}
        {payment.type === 'package' && (
          <>
            <div
              ref={tipProps.refs.setReference}
              className='flex items-center justify-center w-8 h-8 rounded-full bg-green-100 text-green-500'
              {...tipProps.getReferenceProps()}
            >
              <PackageIcon />
            </div>
            {tipProps.isOpen && (
              <Tip
                x={tipProps.x}
                y={tipProps.y}
                placement={tipProps.placement}
                strategy={tipProps.strategy}
                ref={tipProps.refs.setFloating}
                context={tipProps.context}
                arrowRef={tipProps.arrowRef}
                {...tipProps.getFloatingProps()}
              >
                {t('Plugin')}
              </Tip>
            )}
          </>
        )}
        {payment.type === 'layout' && (
          <>
            <div
              ref={tipProps.refs.setReference}
              className='flex items-center justify-center w-8 h-8 rounded-full bg-purple-100 text-purple-500'
              {...tipProps.getReferenceProps()}
            >
              <IconLayout className='fill-transparent' />
            </div>
            {tipProps.isOpen && (
              <Tip
                x={tipProps.x}
                y={tipProps.y}
                placement={tipProps.placement}
                strategy={tipProps.strategy}
                ref={tipProps.refs.setFloating}
                context={tipProps.context as any}
                arrowRef={tipProps.arrowRef}
                {...tipProps.getFloatingProps()}
              >
                {t('Template')}
              </Tip>
            )}
          </>
        )}
      </Td>
      <Td>{formatDate(payment.date)}</Td>
      <Td>{payment.amount}</Td>
      <Td>•••• {payment.cardLast4}</Td>
      <Td>
        <span className='px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800'>
          {payment.status}
        </span>
      </Td>
      <Td className='flex justify-end border-b-0!'>
        <Button onClick={() => onOpenInvoice(payment)} className='inline-flex items-center' icon={<FileTextIcon />}>
          {t('View')}
        </Button>
      </Td>
    </Tr>
  )
}

const BillingHistoryPage = () => {
  const { t } = useTranslation()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [selectedInvoice, setSelectedInvoice] = useState(null)
  const [currentPage, setCurrentPage] = useState(0)
  const [paymentsPerPage, setPaymentsPerPage] = useState(5)

  const itemsPerPageOptions = [
    { id: '5', name: '5' },
    { id: '10', name: '10' },
    { id: '20', name: '20' },
    { id: '50', name: '50' },
    { id: '100', name: '100' },
  ]

  // Calculate pagination
  const indexOfLastPayment = (currentPage + 1) * paymentsPerPage
  const indexOfFirstPayment = currentPage * paymentsPerPage
  const currentPayments = allPayments.slice(indexOfFirstPayment, indexOfLastPayment)

  // Format date
  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
  }

  // Open invoice modal
  const openInvoiceModal = (invoice: any) => {
    setSelectedInvoice(invoice)
    setIsModalOpen(true)
  }

  const handleItemsPerPageChange = (value: string) => {
    setPaymentsPerPage(Number(value))
    setCurrentPage(0)
  }

  return (
    <>
      <div className='rounded-xl shadow-xs mb-4 -mt-4'>
        {/* Table */}
        <div className=' bg-white dark:bg-transparent'>
          <Table className='min-w-full border bdr rounded-lg shadow-sm'>
            <thead>
              <Tr>
                <Th className='px-6 py-3 text-left'>
                  <strong className='font-semibold text-light flex-c'>{t('Type')}</strong>
                </Th>
                <Th className='px-6 py-3 text-left'>
                  <strong className='font-semibold text-light flex-c'>{t('Date')}</strong>
                </Th>
                <Th className='px-6 py-3 text-left'>
                  <strong className='font-semibold text-light flex-c'>{t('Amount')}</strong>
                </Th>
                <Th className='px-6 py-3 text-left'>
                  <strong className='font-semibold text-light flex-c'>{t('Card')}</strong>
                </Th>
                <Th className='px-6 py-3 text-left'>
                  <strong className='font-semibold text-light flex-c'>{t('Status')}</strong>
                </Th>
                <Th className='px-6 py-3 text-right'>
                  <strong className='font-semibold text-light text-right inline-block'>{t('Invoice')}</strong>
                </Th>
              </Tr>
            </thead>
            <tbody>
              {currentPayments.map((payment) => (
                <PaymentRow
                  key={payment.id}
                  payment={payment}
                  formatDate={formatDate}
                  onOpenInvoice={openInvoiceModal}
                  t={t}
                />
              ))}
            </tbody>
          </Table>
        </div>
      </div>
      <div>
        <div className='flex items-end justify-between gap-4'>
          <div>
            <p className='text-sm mt-1 text-gray-500 dark:text-gray-400'>
              {t('Total entries')}: {allPayments.length}
            </p>
          </div>
          <div className='flex justify-end items-end -mt-7'>
            <div className='flex items-center gap-2'>
              <span className='text-sm text-gray-500 dark:text-gray-400'>{t('Rows per page')}:</span>
              <SelectDropdown
                value={itemsPerPageOptions.find((opt) => opt.id === String(paymentsPerPage)) || itemsPerPageOptions[0]}
                onChange={(opt) => handleItemsPerPageChange(opt.id)}
                options={itemsPerPageOptions}
                optionLabel={(opt) => opt.name}
                outlined
                toggleClassName='min-h-[unset]! h-8 bg-white dark:bg-transparent'
              />
            </div>
            <WrappedPagination
              key={`${currentPage}-${paymentsPerPage}`}
              page={currentPage + 1}
              perPage={paymentsPerPage}
              totalCount={allPayments.length}
              onChange={(_from, _to, newPage) => {
                setCurrentPage(newPage - 1)
              }}
            />
          </div>
        </div>
      </div>

      {/* Invoice Modal */}
      {isModalOpen && selectedInvoice && (
        <BillingInvoice invoice={selectedInvoice} onClose={() => setIsModalOpen(false)} formatDate={formatDate} />
      )}
    </>
  )
}

export default BillingHistoryPage
